import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import CountryCode from './CountryCode';
import Button from './Button';
import Header from './Header';
import productsPricing from '../data/productsPricing';
import { findCurrencySymbol } from '../utils/localeUtils';
import './CartPlugin.css';

import cartImage from '../images/shopping-cart.svg';
import companyLogo from '../images/B_longtermtec_logo.png';


const CartPlugin = () => {
    const { sku } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const locale = queryParams.get('locale') || 'en_US';
    const currencySymbol = findCurrencySymbol(locale);
    const [product, setProduct] = useState([]);
    const products = productsPricing[locale] || [];

    // Format price helper
    const formatPrice = (price) => {
        if (!price) return '';
        return `${currencySymbol} ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    //const product = products.find(p => p.sku === sku);


    const [qty, setQty] = useState(product?.qty || 1);
    const [coupon, setCoupon] = useState('');
    const [country, setCountry] = useState('');
    const [subscriptionTerm, setSubscriptionTerm] = useState('Yearly'); // Default to Yearly
    const [price, setPrice] = useState(product?.priceYearly); // Default to Yearly price
    const [cart, setCart] = useState([]);
    const [showCouponModal, setShowCouponModal] = useState(false);
    const [globalTerm, setGlobalTerm] = useState(null); // Global state for selected term
    const [spi, setSpi] = useState(''); // State to store the SPI value
    const [isAdded, setIsAdded] = useState(false); // State for "Added" feedback

    useEffect(() => {
        // Find the product based on the SKU
        const detectedLocale = queryParams.get('locale') || 'en_US'; // Get locale from query params or default to 'en_US'
        const products = productsPricing[detectedLocale] || []; // Get products for the detected locale
        const foundProduct = products.find((p) => p.sku === sku); // Find the product by SKU
        setProduct(foundProduct); // Set the product in state
        setQty(1); // Reset quantity to 1
    }, [sku, queryParams]);

    useEffect(() => {
        // Extract the SPI value from the URL
        const urlParams = new URLSearchParams(window.location.search);
        const spiParam = urlParams.get('spi') || sessionStorage.getItem('spi');
        if (spiParam) {
            setSpi(spiParam);
            sessionStorage.setItem('spi', spiParam); // Store SPI in sessionStorage
        }
    }, []);

    useEffect(() => {
        switch (subscriptionTerm) {
            case 'Monthly':
                setPrice(product?.priceMonthly);
                break;
            case 'Yearly':
                setPrice(product?.priceYearly);
                break;
            case '3 Years':
                setPrice(product?.price3Years);
                break;
            default:
                setPrice(product?.priceYearly);
        }
    }, [subscriptionTerm, product]);

    useEffect(() => {
        const storedCart = JSON.parse(sessionStorage.getItem('cart')) || [];
        setCart(storedCart);

        // If a global term is already set, enforce it
        if (storedCart.length > 0) {
            const cartTerm = storedCart[0].subscriptionTerm;
            setGlobalTerm(cartTerm);
            setSubscriptionTerm(cartTerm); // Also set the subscription term to match
        }
    }, []);

    const handleAddToCart = () => {
        // Use globalTerm if set, otherwise use subscriptionTerm
        const termToUse = globalTerm || subscriptionTerm;
        const priceToUse = getPriceForTerm(product, termToUse);

        // If no global term is set, set it to the current subscription term
        if (!globalTerm) {
            setGlobalTerm(termToUse);
        }

        const newCart = [...cart, { sku, qty, subscriptionTerm: termToUse, price: priceToUse }];
        sessionStorage.setItem('cart', JSON.stringify(newCart));
        setCart(newCart);

        // Show "Added" feedback
        setIsAdded(true);
        setTimeout(() => setIsAdded(false), 1500);
    };

    const handleViewCart = () => {
        navigate(`/cart-summary?locale=${locale}`);
    };

    const handleApplyCoupon = () => {
        setShowCouponModal(false);

        const storedCart = JSON.parse(sessionStorage.getItem('cart')) || [];
        let url = `https://www.plm.automation.siemens.com/offer/configurable_cart?country=${country}`;
        storedCart.forEach((item) => {
            let term = 'yearly'; // Default term
            let length = 1; // Default length

            // Determine the term and length based on the subscription term
            switch (item.subscriptionTerm) {
                case 'Monthly':
                    term = 'monthly';
                    break;
                case 'Yearly':
                    term = 'yearly';
                    length = 1;
                    break;
                case '3 Years':
                    term = 'yearly';
                    length = 3;
                    break;
                default:
                    term = 'yearly';
                    length = 1;
            }

            url += `&sku=${item.sku}&qty=${item.qty}&term=${term}&length=${length}`;
        });

        if (coupon.trim() !== '') {
            url += `&coupon=${encodeURIComponent(coupon.trim())}`;
        }

        // Append the SPI parameter to the URL
        if (spi) {
            url += `&spi=${spi}`;
        }

        url += '&create_cart=Create+Cart';

        window.location.href = url;
    };

    const handleTermSelection = (term) => {
        // Prevent changing the term if a global term is already set
        if (!globalTerm) {
            setSubscriptionTerm(term);
        }
    };

    const isAddToCartDisabled = () => {
        // Disable "Add to Cart" if the global term is not available for the current product
        if (globalTerm) {
            const availableTerms = [
                { term: 'Monthly', price: product?.priceMonthly },
                { term: 'Yearly', price: product?.priceYearly },
                { term: '3 Years', price: product?.price3Years }
            ].filter(option => option.price > 0).map(option => option.term);

            return !availableTerms.includes(globalTerm);
        }
        return false;
    };

    const handleClearCart = () => {
        sessionStorage.removeItem('cart'); // Clear cart data from sessionStorage
        setCart([]); // Reset cart state
        setGlobalTerm(null); // Reset the global term
    };

    const getPriceForTerm = (product, term) => {
        switch (term) {
            case 'Monthly': return product.priceMonthly;
            case 'Yearly': return product.priceYearly;
            case '3 Years': return product.price3Years;
            default: return product.priceYearly;
        }
    };

    return (
        <div>
            <Header title="Shopping Cart" logo={companyLogo} />

            <div className="cart-plugin-container">
                <CountryCode onCountryChange={setCountry} />
                {product && (
                    <div className="product-info">
                        <img
                            className="product-image"
                            src={product.image}
                            alt={product.name || 'Product'}
                        />
                        <h2 className="product-name">{product.name}</h2>
                        {product.description && (
                            <p className="product-description">{product.description}</p>
                        )}
                        {product.prerequisites && product.prerequisites.length > 0 && (
                            <div className="prerequisite-warning">
                                <span className="warning-icon">⚠</span>
                                <span>Prerequisite product required.</span>
                            </div>
                        )}
                    </div>
                )}

                <form className="cart-form">
                    <div className="form-group">
                        <label>Quantity:</label>
                        <input
                            type="number"
                            value={product?.isTrial ? 1 : qty}
                            min="1"
                            onChange={(e) => setQty(parseInt(e.target.value))}
                            className="form-input"
                            disabled={product?.isTrial}
                        />
                    </div>

                    {product?.isTrial ? (
                        <div className="form-group">
                            <p><strong>30 Days Free Trial</strong></p>
                        </div>
                    ) : (
                        <div className="form-group">
                            <label>Price:</label>
                            <p>{formatPrice(getPriceForTerm(product, globalTerm || subscriptionTerm))}</p>
                        </div>
                    )}
                    {!product?.isTrial && (
                        <div className="form-group">
                            <label>Subscription Term:</label>
                            <div className="subscription-buttons">
                                {[
                                    { term: 'Monthly', price: product?.priceMonthly },
                                    { term: 'Yearly', price: product?.priceYearly },
                                    { term: '3 Years', price: product?.price3Years }
                                ]
                                    .filter(option => option.price > 0)
                                    .filter(option => !globalTerm || option.term === globalTerm)
                                    .map(option => (
                                        <button
                                            key={option.term}
                                            type="button"
                                            className={`subscription-button ${subscriptionTerm === option.term ? 'active' : ''}`}
                                            onClick={() => handleTermSelection(option.term)}
                                        >
                                            {option.term}
                                        </button>
                                    ))}
                            </div>

                            {globalTerm && (
                                <p className="global-term-message">
                                    You have selected the first product with the "{globalTerm}" term, so only this term is available for other products.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Button Actions Section */}
                    <div className="button-actions">
                        <button
                            type="button"
                            className={`primary-button ${isAdded ? 'added' : ''}`}
                            onClick={() => {
                                if (product?.isTrial) {
                                    const spi = locale === 'en_CA' ? '4077332' : '4431814';
                                    if (product.sku === 'SETRIAL') {
                                        window.location.href = `https://www.plm.automation.siemens.com/solid-edge/trial/en-us/home?pid=0013000000gLhUFAA0&spi=${spi}`;
                                    } else {
                                        window.location.href = `https://trials.sw.siemens.com/en-US/trials/nx-x-online-cad?pid=0013000000gLhUFAA0&spi=${spi}`;
                                    }
                                } else {
                                    handleAddToCart();
                                }
                            }}
                            disabled={!product?.isTrial && (isAddToCartDisabled() || isAdded)}
                        >
                            {product?.isTrial ? 'Proceed to Checkout' : (isAdded ? '✓ Added!' : 'Add to Cart')}
                        </button>

                        {cart.length > 0 && (
                            <>
                                <div className="secondary-buttons">
                                    <button
                                        type="button"
                                        className="secondary-button"
                                        onClick={() => navigate(locale === 'en_CA' ? '/cad' : '/usd')}
                                    >
                                        Continue Shopping
                                    </button>
                                    <button
                                        type="button"
                                        className="secondary-button"
                                        onClick={handleViewCart}
                                    >
                                        View Cart
                                    </button>
                                </div>

                                <div className="cart-status">
                                    <span className="cart-count">
                                        <img src={cartImage} alt="Cart" className="cart-count-icon" />
                                        {cart.length} item{cart.length !== 1 ? 's' : ''} in cart
                                    </span>
                                    <button
                                        type="button"
                                        className="clear-cart-btn"
                                        onClick={handleClearCart}
                                    >
                                        <img src={cartImage} alt="Clear" />
                                        Clear
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </form>
            </div >

            {/* Coupon Code Modal */}
            {
                showCouponModal && (
                    <div className="modal-overlay">
                        <div className="modal-content" style={{ position: 'relative' }}>
                            <button
                                className="modal-close"
                                onClick={() => setShowCouponModal(false)}
                                aria-label="Close"
                            >
                                &times;
                            </button>
                            <h2>Enter Coupon Code</h2>
                            <input
                                type="text"
                                value={coupon}
                                onChange={(e) => setCoupon(e.target.value)}
                                placeholder="Enter coupon code"
                                className="form-input"
                            />
                            <div className="modal-actions">
                                <button className="apply-button" onClick={handleApplyCoupon}>Apply</button>
                                <button className="skip-button" onClick={handleApplyCoupon}>Skip</button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >

    );
};

export default CartPlugin;
